import { useEffect, useRef, useState } from "react";
import {
  api,
  authorityAdvanceBatchEhrlich,
  jfetch,
  type AgentRoomFeedItemView,
  type BatchAdvanceResponse,
  type MissionCheckpointView,
  type MissionDetailResponse,
  type MissionEvidenceItemView,
  type MissionItemView,
} from "../api";
import { queueStateClass, toneClass, toneTextClass, phasenZustandKlasse } from "./queueStateStyling";
import { statusCopy, statusErklaerung, statusLabel } from "../domain/statusCopy";
import { missionsPhasen, PHASEN_ZUSTAND_LABEL } from "./missionPhases";

/**
 * OWNEXP Slice 6 — Mission Detail auf Owner-Niveau (R-MR-1..5).
 *
 * Primär: Ziel, Zustand, Phase, aktueller Run, Writer/Lease, Modell, Ressourcen,
 * Dependencies, Checkpoints, Tests/Verifier/Red-Team-Spuren, Evidence, Deployment,
 * Next Action, Owner Action — in Owner-Sprache. Phasen-Streifen
 * Aufnahme→Umfang→Startbereit→Writer-Lease→Ausführung→Tests→Prüfung→
 * Auslieferung→Beobachtung→Abschluss, ehrlich aus realen Signalen abgeleitet
 * (missionPhases.ts); Phasen ohne Messsignal bleiben „kein separates Signal".
 * Technische IDs sekundar (Drilldown/title). Kein CoT — nur Metadaten.
 */

type Tab = "uebersicht" | "ereignisse" | "checkpoints" | "evidence" | "findings";

const FEED_KIND_LABEL: Record<string, string> = {
  message: "Nachricht",
  run_event: "Run-Ereignis",
  evidence_ref: "Beleg-Referenz",
  open_gate: "Offenes Gate",
  finding: "Finding",
};

/** CP-16: Verifikationsstand des Beleg-Inhalts in Owner-Sprache. */
const CONTENT_BINDING_LABEL: Record<string, string> = {
  VERIFIED_CONTENT: "Inhalt verifiziert",
  REFERENCE_ONLY: "Nur Referenz — Inhalt ungeprüft",
  UNKNOWN: "Prüfstand offen",
};

function bindingLabel(contentBinding: string | null | undefined): string {
  const schluessel = String(contentBinding ?? "UNKNOWN");
  return CONTENT_BINDING_LABEL[schluessel] ?? "Prüfstand offen";
}

const PRIO_LABEL: Record<string, string> = {
  urgent: "Dringend",
  high: "Hoch",
  normal: "Normal",
  low: "Niedrig",
};

interface FindingView {
  finding_id: string;
  severity: string;
  finding_class: string;
  found_by: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface FindingsResponse {
  ok: boolean;
  findings: FindingView[];
}

export function MissionDetailDialog({
  missionId,
  onClose,
}: {
  missionId: string;
  onClose: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<Tab>("uebersicht");
  const [detail, setDetail] = useState<MissionDetailResponse | null>(null);
  const [feed, setFeed] = useState<AgentRoomFeedItemView[]>([]);
  const [checkpoints, setCheckpoints] = useState<MissionCheckpointView[]>([]);
  const [evidence, setEvidence] = useState<MissionEvidenceItemView[]>([]);
  const [findings, setFindings] = useState<FindingView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // OWNEXP Red-Team M8: aria-modal verpflichtet Tastatur-Ausweg — Escape
  // schließt den Dialog. RECIPE-V2 E (DIALOG_FOCUS_LIFECYCLE): Initialfokus
  // auf den Dialog und Rückgabe EXAKT an den auslösenden CTA (der Fokus
  // beim Öffnen — die Karte, die den Dialog auslöste).
  // S0 — Owner-Gate-Laden + Batch-Advance aus dem Detail heraus.
  // S0.1: EIN Bundle je Familie/Einzelmission — der Dialog sucht das Bundle,
  // dessen EINGEFRORENER Snapshot die angezeigte Mission enthaelt.
  const [ownerGateStatus, setOwnerGateStatus] = useState<{
    state: "LOADING" | "LIVE" | "UNAVAILABLE";
    gate: { gateId: string; missionIds: string[] } | null;
  }>({ state: "LOADING", gate: null });
  const [ownerGateBusy, setOwnerGateBusy] = useState(false);
  const [ownerGateErgebnis, setOwnerGateErgebnis] = useState<BatchAdvanceResponse | null>(null);
  const ladeOwnerGate = (): void => {
    api.ownerGateStatus().then((r) => {
      const treffer = (r.bundles ?? []).find((b) => b.missionIds.includes(missionId)) ?? null;
      setOwnerGateStatus(
        r.state === "LIVE"
          ? { state: "LIVE", gate: treffer ? { gateId: treffer.gateId, missionIds: treffer.missionIds } : null }
          : r.state === "WARMING"
            ? { state: "LOADING", gate: null } // S0.3: Aufwaermphase ist kein Fehler
            : { state: "UNAVAILABLE", gate: null },
      );
    }).catch(() => setOwnerGateStatus({ state: "UNAVAILABLE", gate: null }));
  };
  useEffect(ladeOwnerGate, [missionId]);
  const advanceShadow = (): void => {
    setOwnerGateBusy(true);
    authorityAdvanceBatchEhrlich(
      "Freigabe SHADOW aus Missionsdetail",
      ownerGateStatus.gate?.gateId ?? "",
    )
      .then((r) => {
        setOwnerGateErgebnis(r);
        ladeOwnerGate();
      })
      .catch((err) => {
        setOwnerGateErgebnis({
          ok: false,
          message: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => setOwnerGateBusy(false));
  };
  const dialogCardRef = useRef<HTMLDivElement | null>(null);
  const vorherigerFokusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    vorherigerFokusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    dialogCardRef.current?.focus();
    return () => {
      vorherigerFokusRef.current?.focus?.();
    };
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let abgebaut = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.missionDetail(missionId),
      api.agentRoomFeed(missionId, 200).catch(() => null),
      api.missionCheckpoints(missionId, 100).catch(() => null),
      api.missionEvidence(missionId, 100).catch(() => null),
      jfetch<FindingsResponse>(`/api/findings?mission_id=${encodeURIComponent(missionId)}&limit=50`).catch(() => null),
    ])
      .then(([d, f, c, e, fi]) => {
        if (abgebaut) return;
        setDetail(d);
        setFeed(f?.items ?? []);
        setCheckpoints(c?.checkpoints ?? []);
        setEvidence(e?.evidence ?? []);
        setFindings(fi?.findings ?? []);
      })
      .catch((err) => {
        if (!abgebaut) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!abgebaut) setLoading(false);
      });
    return () => {
      abgebaut = true;
    };
  }, [missionId]);

  // S0 — strukturierter Owner-Gate-Zustand aus dem Gate-Lesepfad. Fehler
  // hier bedeuten "unbekannt", nicht "nicht erforderlich": required bleibt
  // false, aber der Fallback-Text wird dann nicht als Wahrheit verkauft.
  const ownerGateErforderlich = ownerGateStatus.state === "LIVE"
    && ownerGateStatus.gate !== null
    && ownerGateStatus.gate.missionIds.includes(missionId);

  const mission = detail?.mission;
  const items = detail?.items ?? [];
  const elapsed = mission ? seitWieLange(String(mission.created_at ?? "")) : null;
  const ownerGatesImFeed = feed.filter((f) => f.kind === "open_gate");

  // CP-16: Evidence ist first-class in der Missions-Timeline — Belege
  // erscheinen chronologisch zwischen den Run-Ereignissen (neueste zuerst).
  const timeline: Array<
    | { zeit: string; eintrag: AgentRoomFeedItemView }
    | { zeit: string; beleg: MissionEvidenceItemView }
  > = [
    ...feed.map((f) => ({ zeit: f.occurredAt, eintrag: f })),
    ...evidence.map((e) => ({ zeit: e.createdAt, beleg: e })),
  ].sort((a, b) => Date.parse(b.zeit) - Date.parse(a.zeit));

  const phasen = missionsPhasen({
    mission: mission ? { status: String(mission.status ?? ""), created_at: String(mission.created_at ?? ""), updated_at: String(mission.updated_at ?? "") } : null,
    items,
    feed,
    evidence,
    findings,
  });

  // ── Kernfakten (R-MR-1): Zustand, Writer/Lease, Modell, Ressourcen, Agent ──
  const leaseItem = items.find((it) => it.lease_holder) ?? null;
  const leaseRest = leaseItem?.lease_expires_at ? restZeit(String(leaseItem.lease_expires_at)) : null;
  const modell = modellText(items);
  const ressourcen = ressourcenText(items);
  const agent = agentAusFeed(feed);
  const aktivePhase = phasen.find((p) => p.zustand === "aktiv") ?? null;

  // ── Next Action / Owner Action (R-MR-4/5) ──
  const wartendAufOwner = items.filter((it) => it.queue_state === "WAIT_OWNER" || it.queue_state === "WAIT_HUMAN");
  const fehlgeschlagen = items.filter((it) => it.queue_state === "FAILED");
  const nextAction: string | null = (() => {
    if (wartendAufOwner.length > 0) return `SIMO wartet auf deine Entscheidung (${wartendAufOwner[0]?.queue_state === "WAIT_OWNER" ? "Human Gate" : "menschliche Freigabe"}) — Entscheidung im Bereich Human Gates treffen.`;
    if (fehlgeschlagen.length > 0) return "Fehlgeschlagenes Item: Recovery-Entscheidung oder Wiederholung prüfen (Queue-Drilldown).";
    if (leaseItem) return `Writer ${String(leaseItem.lease_holder)} arbeitet mit exklusiver Schreibberechtigung${leaseRest ? ` (noch ${leaseRest})` : ""}.`;
    if (items.length > 0) return "Nächstes Item wartet auf Writer-Zuteilung durch den Supervisor (automatisch).";
    return null;
  })();
  const ownerAction = ownerGateErforderlich
    ? "JA — Freigabe für SHADOW erforderlich."
    : wartendAufOwner.length > 0
    ? "JA — offene Entscheidung erforderlich."
    : fehlgeschlagen.length > 0
      ? "Prüfen: Fehlerursache im Findings-/Queue-Drilldown nachvollziehen."
      : "Nein — SIMO arbeitet autonom weiter.";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Mission ${missionId}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogCardRef}
        tabIndex={-1}
        className="card w-full sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col !p-0 focus:outline-none"
        data-testid="mission-detail-dialog"
      >
        <div className="p-4 border-b border-jarvis-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold truncate" title={String(mission?.goal ?? missionId)}>
              {String(mission?.goal ?? missionId).slice(0, 100)}
              {String(mission?.goal ?? "").length > 100 ? "…" : ""}
            </h2>
            <div className="text-[11px] text-jarvis-muted font-mono mt-0.5">{missionId}</div>
            {mission && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-jarvis-muted">
                <span>
                  Zustand:{" "}
                  <strong className="text-jarvis-text">{statusLabel(String(mission.status)) ?? String(mission.status)}</strong>
                </span>
                <span>
                  Priorität: <strong className="text-jarvis-text">{prioText(items)}</strong>
                </span>
                {elapsed && (
                  <span>
                    Laufzeit: <strong className="text-jarvis-text">{elapsed}</strong>
                  </span>
                )}
                <span>
                  Teilaufgaben: <strong className="text-jarvis-text">{items.length}</strong>
                </span>
                <span>
                  Checkpoints: <strong className="text-jarvis-text">{detail?.checkpointCount ?? checkpoints.length}</strong>
                </span>
                {ownerGatesImFeed.length > 0 && (
                  <span className={toneTextClass("critical")}>Owner-Entscheidung offen: {ownerGatesImFeed.length}</span>
                )}
              </div>
            )}
          </div>
          <button className="btn shrink-0" onClick={onClose} aria-label="Mission-Detail schließen">
            Schließen
          </button>
        </div>

        {/* Phasen-Streifen (R-MR-2): ehrliche Ableitung, kein Fortschritts-Theater */}
        <div
          className="flex gap-1.5 overflow-x-auto border-b border-jarvis-border px-3 py-2.5" tabIndex={0} role="group"
          aria-label="Missions-Phasen"
          data-testid="mission-phase-strip"
        >
          {phasen.map((p, i) => (
            <div
              key={p.id}
              className={`flex min-w-max flex-col gap-0.5 rounded-md border px-2 py-1 text-[10px] leading-tight ${phasenZustandKlasse(p.zustand)}`}
              title={`${p.hinweis}${p.zeitstempel ? ` (${new Date(p.zeitstempel).toLocaleString("de-DE")})` : ""}`}
              data-testid={`phase-${p.id}`}
            >
              <span className="font-semibold">
                {i + 1}. {p.label}
              </span>
              <span>
                {p.zustand === "aktiv" ? "läuft jetzt" : PHASEN_ZUSTAND_LABEL[p.zustand]}
                {p.zeitstempel ? ` · ${new Date(p.zeitstempel).toLocaleDateString("de-DE")}` : ""}
              </span>
            </div>
          ))}
        </div>

        {/* Kernfakten (R-MR-1): Writer/Lease/Modell/Ressourcen/Agent auf einen Blick */}
        <div className="grid grid-cols-2 gap-2 border-b border-jarvis-border p-3 text-xs md:grid-cols-4" data-testid="mission-facts">
          <Fakt label="Aktuelle Phase" wert={aktivePhase ? aktivePhase.label : phasen.filter((p) => p.zustand === "erreicht").at(-1)?.label ?? "—"} hinweis={aktivePhase?.hinweis ?? "Letzte erreichte Phase."} />
          <Fakt
            label="Writer / Lease"
            wert={leaseItem ? String(leaseItem.lease_holder) : "Kein Writer aktiv"}
            hinweis={leaseItem?.lease_expires_at ? `Schreibberechtigung läuft ${new Date(String(leaseItem.lease_expires_at)).toLocaleTimeString("de-DE")} ab${leaseRest ? ` (${leaseRest})` : ""}` : "Exklusive Schreibberechtigung wird vom Supervisor vergeben."}
          />
          <Fakt label="Modell" wert={modell} hinweis="Modell-Politik der Teilaufgaben (Scheduling-Readback)." />
          <Fakt label="Ressourcen" wert={ressourcen} hinweis="Beanpruchte Ressourcenklassen (z. B. Schreibrecht)." />
          <div className="col-span-2">
            <Fakt label="Ausführender Agent / Runtime" wert={agent ?? "Kein Agent-Signal im Feed"} hinweis="Letzter Akteur aus dem Run-Ereignis-Feed (Metadaten, kein CoT)." />
          </div>
        </div>

        {/* Next Action / Owner Action (R-MR-4/5) */}
        {nextAction && (
          <div
            className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-jarvis-border px-3 py-2 text-xs ${wartendAufOwner.length > 0 ? toneClass("waiting") : "text-jarvis-muted"}`}
            data-testid="mission-next-action"
          >
            <span>
              <strong className="text-jarvis-text">Nächster Schritt:</strong> {nextAction}
            </span>
            <span className={wartendAufOwner.length > 0 || ownerGateErforderlich ? "font-semibold" : ""}>
              <strong className="text-jarvis-text">Owner-Aktion:</strong> {ownerAction}
            </span>
            {ownerGateErforderlich && (
              <span className="flex items-center gap-2">
                <button
                  className="btn-primary text-xs"
                  disabled={ownerGateBusy}
                  onClick={() => void advanceShadow()}
                  data-testid="mission-owner-gate-cta"
                >
                  {ownerGateBusy ? "…" : "In SHADOW aufnehmen"}
                </button>
                {ownerGateErgebnis && (
                  <span data-testid="mission-owner-gate-ergebnis" className="text-jarvis-muted">
                    {(ownerGateErgebnis.results ?? [])
                      .map((r) => `${r.ok ? "ok" : "FEHLER"}: ${r.missionId}${r.ok && r.receiptId ? ` (Receipt ${r.receiptId.slice(0, 12)}…)` : r.message ? ` — ${r.message}` : ""}`)
                      .join(" · ")}
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        <div className="flex border-b border-jarvis-border text-xs" role="tablist">
          {(
            [
              ["uebersicht", "Übersicht"],
              ["ereignisse", `Ereignisse (${feed.length})`],
              ["checkpoints", `Checkpoints (${checkpoints.length})`],
              ["evidence", `Evidence (${evidence.length})`],
              ["findings", `Findings (${findings.length})`],
            ] as Array<[Tab, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              className={`px-3 py-2 ${tab === key ? "bg-jarvis-accent/20 text-jarvis-text font-medium" : "text-jarvis-muted"}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto p-4 text-sm" tabIndex={0} aria-label="Mission-Inhalt" data-testid={`mission-tab-${tab}`}>
          {loading && <div className="text-jarvis-muted">Lade Missionsprotokoll…</div>}
          {error && (
            <div className={toneTextClass("danger")} role="alert">
              Mission nicht abrufbar: {error}
            </div>
          )}
          {!loading && !error && tab === "uebersicht" && (
            <div className="space-y-2">
              {items.length === 0 && (
                <div className="text-jarvis-muted">Keine Teilaufgaben an dieser Mission.</div>
              )}
              {items.map((it) => (
                <TeilaufgabeZeile key={it.id} it={it} />
              ))}
            </div>
          )}
          {!loading && !error && tab === "ereignisse" && (
            <div className="space-y-1.5">
              {timeline.length === 0 && <div className="text-jarvis-muted">Keine Ereignisse projiziert.</div>}
              {timeline.map((t) =>
                "eintrag" in t ? (
                  <div key={t.eintrag.id} className="flex gap-3 items-baseline border-b border-jarvis-border/40 last:border-0 pb-1.5">
                    <span className="text-[10px] text-jarvis-muted whitespace-nowrap font-mono">
                      {new Date(t.zeit).toLocaleString("de-DE")}
                    </span>
                    <span className="text-[11px] font-medium text-jarvis-accent whitespace-nowrap">
                      {FEED_KIND_LABEL[t.eintrag.kind] ?? t.eintrag.kind}
                      {typeof t.eintrag.role === "string" ? ` · ${t.eintrag.role}` : ""}
                      {typeof t.eintrag.type === "string" ? ` · ${t.eintrag.type}` : ""}
                    </span>
                    <span className="text-[11px] text-jarvis-muted truncate">
                      {typeof t.eintrag.summary === "string" && t.eintrag.summary
                        ? String(t.eintrag.summary).slice(0, 120)
                        : typeof t.eintrag.actorClass === "string"
                          ? t.eintrag.actorClass
                          : ""}
                    </span>
                  </div>
                ) : (
                  <div
                    key={`ev-${String(t.beleg.evidenceId)}`}
                    className="flex gap-3 items-baseline border-b border-jarvis-border/40 last:border-0 pb-1.5"
                    data-testid="mission-timeline-evidence"
                  >
                    <span className="text-[10px] text-jarvis-muted whitespace-nowrap font-mono">
                      {new Date(t.zeit).toLocaleString("de-DE")}
                    </span>
                    <span className="text-[11px] font-medium text-jarvis-accent whitespace-nowrap">
                      Beleg · {t.beleg.evidenceKind}
                    </span>
                    <span className="text-[11px] text-jarvis-muted truncate">
                      <span data-testid="mission-timeline-evidence-binding">{bindingLabel(t.beleg.contentBinding)}</span>
                      {" · "}
                      {t.beleg.subjectType}:{t.beleg.subjectId}
                    </span>
                  </div>
                ),
              )}
            </div>
          )}
          {!loading && !error && tab === "checkpoints" && (
            <div className="space-y-1.5">
              {checkpoints.length === 0 && (
                <div className="text-jarvis-muted">Keine Checkpoints (entsteht bei Rotation/Crash-Resume).</div>
              )}
              {checkpoints
                .slice()
                .reverse()
                .map((c) => (
                  <div key={c.checkpoint_id} className="flex gap-3 items-baseline text-xs border-b border-jarvis-border/40 last:border-0 pb-1.5">
                    <span className="font-mono text-jarvis-muted whitespace-nowrap">
                      #{c.seq} · {new Date(c.created_at).toLocaleString("de-DE")}
                    </span>
                    <span className="font-medium">{c.current_block}</span>
                    <span className="font-mono text-jarvis-muted">{c.mission_state}</span>
                  </div>
                ))}
            </div>
          )}
          {!loading && !error && tab === "evidence" && (
            <div className="space-y-1.5">
              {evidence.length === 0 && <div className="text-jarvis-muted">Keine Belege verknüpft.</div>}
              {evidence.map((e) => (
                <div key={String(e.evidenceId)} className="text-xs border-b border-jarvis-border/40 last:border-0 pb-1.5" data-testid="mission-evidence-row">
                  <div className="flex gap-3 items-baseline">
                    <span className="font-mono text-jarvis-muted">#{String(e.evidenceId)}</span>
                    <span className="font-medium">{e.evidenceKind}</span>
                    <span
                      className={`whitespace-nowrap ${
                        String(e.contentBinding ?? "") === "VERIFIED_CONTENT"
                          ? "text-emerald-400 font-medium"
                          : String(e.contentBinding ?? "") === "REFERENCE_ONLY"
                            ? "text-jarvis-muted"
                            : "text-amber-400"
                      }`}
                      data-testid="mission-evidence-binding"
                    >
                      {bindingLabel(e.contentBinding)}
                    </span>
                    <span className="text-jarvis-muted truncate">{e.subjectType}:{e.subjectId}</span>
                    <span className="text-jarvis-muted whitespace-nowrap">{new Date(e.createdAt).toLocaleDateString("de-DE")}</span>
                  </div>
                  <div className="text-[10px] text-jarvis-muted font-mono truncate">
                    {e.provenance ? `provenance=${e.provenance} ` : ""}
                    {e.producer ? `producer=${e.producer} ` : ""}
                    {e.sha256 ? `sha256=${String(e.sha256).slice(0, 12)}… ` : ""}
                    {e.sizeBytes != null ? `${e.sizeBytes} B` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && !error && tab === "findings" && (
            <div className="space-y-1.5">
              {findings.length === 0 && (
                <div className="text-jarvis-muted">
                  Keine Findings an dieser Mission — keine bekannten Belastungen durch Prüfer oder Red Team.
                </div>
              )}
              {findings.map((f) => (
                <div key={f.finding_id} className="flex flex-wrap gap-x-3 gap-y-0.5 items-baseline text-xs border-b border-jarvis-border/40 last:border-0 pb-1.5" data-testid="mission-finding-row">
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${toneClass(f.severity === "CRITICAL" || f.severity === "HIGH" ? "critical" : "warning")}`}>
                    {f.severity}
                  </span>
                  <span className="font-medium">{f.finding_class}</span>
                  <span className="text-jarvis-muted">gefunden von {f.found_by ?? "—"}</span>
                  <span className="text-jarvis-muted">{f.status}</span>
                  <span className="text-jarvis-muted whitespace-nowrap font-mono">{new Date(f.updated_at).toLocaleDateString("de-DE")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Teilaufgaben-Zeile (R-MR-3): Owner-Sprache primär, Rohcode sekundar, Warum-Zeile, Lease/Scope-Details. */
function TeilaufgabeZeile({ it }: { it: MissionItemView }): JSX.Element {
  const state = it.queue_state ?? "";
  const eintrag = statusCopy(state);
  // SINGLE_TRUTH: kanonische Server-Erklärung vorrangig (erklaereWartegrund);
  // das statische Register ist nur noch Fallback für Antworten ohne Serverfeld.
  const klaerung = it.warum_wartend ?? statusErklaerung(state) ?? it.queue_state_reason ?? null;
  const prio = PRIO_LABEL[it.priority] ?? it.priority;
  const leaseRest = it.lease_expires_at ? restZeit(String(it.lease_expires_at)) : null;
  return (
    <div className="border-b border-jarvis-border/60 last:border-0 pb-2 last:pb-0" data-testid={`mission-item-${it.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">{it.title}</div>
          <div className="text-[11px] text-jarvis-muted font-mono truncate">{it.id}</div>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${queueStateClass(state)}`}
          title={`${state || "kein Zustand"}${it.queue_state_reason ? ` · ${it.queue_state_reason}` : ""}`}
        >
          {eintrag?.label ?? "Zustand unbekannt"}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-jarvis-muted">
        {klaerung && <span className="max-w-full">Warum: {klaerung}</span>}
        <span>Priorität: {prio}</span>
        {it.writer_scope && (
          <span>
            Schreibbereich: {it.writer_scope}
            {it.scope_verified === 1 ? " (verifiziert)" : it.scope_verified === 0 ? " (nicht verifiziert)" : ""}
          </span>
        )}
        {it.lease_holder && (
          <span>
            Writer: {it.lease_holder}
            {leaseRest ? ` (noch ${leaseRest})` : ""}
          </span>
        )}
      </div>
    </div>
  );
}

function Fakt({ label, wert, hinweis }: { label: string; wert: string; hinweis?: string }): JSX.Element {
  const testid = `mission-fact-${label.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`;
  return (
    <div className="min-w-0 rounded-md border border-white/10 bg-black/20 p-2" data-testid={testid}>
      <span className="block text-[10px] uppercase tracking-wider text-jarvis-muted">{label}</span>
      <strong className="block truncate text-xs text-jarvis-text" title={wert}>
        {wert}
      </strong>
      {hinweis && <span className="mt-0.5 block text-[10px] leading-snug text-jarvis-muted">{hinweis}</span>}
    </div>
  );
}

function prioText(items: MissionItemView[]): string {
  const dringendste = items.find((it) => it.priority === "urgent") ?? items.find((it) => it.priority === "high") ?? items[0];
  return dringendste ? (PRIO_LABEL[dringendste.priority] ?? dringendste.priority) : "—";
}

function modellText(items: MissionItemView[]): string {
  for (const it of items) {
    const roh = it.model_policy_json;
    if (!roh) continue;
    try {
      const policy = JSON.parse(roh) as { tier_floor?: string; premium_reason?: string };
      if (policy.tier_floor) {
        return policy.tier_floor === "premium"
          ? `Premium-Klasse${policy.premium_reason ? ` (${policy.premium_reason})` : ""}`
          : policy.tier_floor;
      }
    } catch {
      // kaputte Policy-JSON bewusst roh zeigen (Drilldown-Prinzip)
      return roh.slice(0, 40);
    }
  }
  return "Keine Modell-Politik gesetzt";
}

function ressourcenText(items: MissionItemView[]): string {
  const klassen = new Set<string>();
  for (const it of items) {
    const roh = it.resource_class_json;
    if (!roh) continue;
    try {
      const liste = JSON.parse(roh) as unknown;
      if (Array.isArray(liste)) liste.forEach((k) => klassen.add(String(k)));
    } catch {
      // ignoriert — Ressourcen bleiben sekundär
    }
  }
  if (klassen.size === 0) return "Keine Ressourcenklasse gesetzt";
  return [...klassen].map((k) => statusLabel(k) ?? k).join(", ");
}

function agentAusFeed(feed: AgentRoomFeedItemView[]): string | null {
  const runEvents = feed.filter((f) => f.kind === "run_event");
  for (let i = runEvents.length - 1; i >= 0; i -= 1) {
    const f: AgentRoomFeedItemView | undefined = runEvents[i];
    if (!f) continue;
    const akteur = typeof f.actorClass === "string" && f.actorClass ? f.actorClass : typeof f.role === "string" ? f.role : null;
    if (akteur) return akteur;
  }
  return null;
}

function seitWieLange(iso: string): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
}

function restZeit(iso: string): string | null {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return "abgelaufen";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}
